import { createSlice } from '@reduxjs/toolkit';

/**
 * Whether the API is answering at all.
 *
 * A fact about the world, not about any screen: a component only ever learns
 * that *its* request failed, and from in there a dead backend and a broken
 * endpoint look the same. Somebody has to hold the difference, and this is it.
 *
 * It holds no timers and asks nothing on its own. The flag moves with the
 * traffic the app was already making — down when a request gets no answer, up
 * with the next answer of any kind. Nothing is persisted: the state of the
 * network is something you find out, not something you restore from disk.
 */
const initialState = {
  isApiReachable: true,
  /** The user went back to the app from the fallback screen to try again. */
  retried: false,
  /** The first request after that retry got no answer either. */
  stillUnreachable: false,
};

const connectionSlice = createSlice({
  name: 'connection',
  initialState,
  reducers: {
    apiUnreachable: (state) => {
      if (state.isApiReachable) {
        state.isApiReachable = false;
        state.stillUnreachable = state.retried;
        state.retried = false;
      }
    },

    apiReachable: (state) => {
      state.isApiReachable = true;
      state.retried = false;
      state.stillUnreachable = false;
    },

    /**
     * "Reintentar" on the fallback screen. It asks the server nothing of its
     * own: the app comes back, and the requests it makes anyway say whether
     * the server is up — any answer keeps it, no answer brings the screen back.
     */
    connectionRetried: (state) => {
      state.isApiReachable = true;
      state.retried = true;
    },

    /**
     * A change sent while signed in got no answer. It may or may not have been
     * applied, so unlike `apiUnreachable` it does not take the screen — that
     * would unmount the dialog it came from. Nothing to keep here: the feedback
     * listener turns it into the "puede haberse aplicado" warning.
     */
    mutationUnconfirmed: () => {},
  },
});

export const {
  apiUnreachable,
  apiReachable,
  connectionRetried,
  mutationUnconfirmed,
} = connectionSlice.actions;

export const selectIsApiReachable = (state) => state.connection.isApiReachable;

export default connectionSlice.reducer;
